import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { bedStatusAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { BedDouble, User, Wrench, Clock, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const STATUS_CONFIG = {
  available: { label: 'Vacant', color: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: BedDouble },
  vacant: { label: 'Vacant', color: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: BedDouble },
  occupied: { label: 'Occupied', color: 'bg-blue-500', light: 'bg-blue-50 border-blue-200 text-blue-700', icon: User },
  maintenance: { label: 'Maintenance', color: 'bg-amber-500', light: 'bg-amber-50 border-amber-200 text-amber-700', icon: Wrench },
  reserved: { label: 'Reserved', color: 'bg-purple-500', light: 'bg-purple-50 border-purple-200 text-purple-700', icon: Clock },
};

const BedStatusPage = () => {
  const { hospital } = useAuth();
  const navigate = useNavigate();
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWard, setSelectedWard] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = hospital?.id ? { hospital_id: hospital.id } : {};
      const res = await bedStatusAPI.getBoard(params);
      setWards(res.data || []);
    } catch { toast.error('Failed to load bed status'); }
    setLoading(false);
  }, [hospital?.id]);

  useEffect(() => { load(); }, [load]);

  const totalBeds = wards.reduce((a, w) => a + (w.total_beds || 0), 0);
  const totalOccupied = wards.reduce((a, w) => a + (w.occupied || 0), 0);
  const totalVacant = wards.reduce((a, w) => a + (w.vacant || 0), 0);
  const totalMaint = wards.reduce((a, w) => a + (w.maintenance || 0), 0);
  const occupancyRate = totalBeds > 0 ? Math.round((totalOccupied / totalBeds) * 100) : 0;

  const handleStatusChange = async (wardId, bedNumber, newStatus) => {
    try {
      await bedStatusAPI.updateBed(wardId, bedNumber, { status: newStatus });
      toast.success(`Bed ${bedNumber} updated to ${newStatus}`);
      load();
    } catch { toast.error('Failed to update bed'); }
  };

  return (
    <div className="space-y-5" data-testid="bed-status-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Bed Status Dashboard</h1>
        <p className="text-sm text-slate-500 mt-0.5">Real-time bed occupancy. <span className="font-medium text-emerald-600">Click a vacant bed</span> to admit a patient · <span className="font-medium text-blue-600">Hover an occupied bed</span> to see the patient.</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-800">{totalBeds}</p>
          <p className="text-[10px] text-slate-500 uppercase font-semibold">Total Beds</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{totalOccupied}</p>
          <p className="text-[10px] text-blue-600 uppercase font-semibold">Occupied</p>
        </div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 text-center">
          <p className="text-2xl font-bold text-emerald-700">{totalVacant}</p>
          <p className="text-[10px] text-emerald-600 uppercase font-semibold">Vacant</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center">
          <p className="text-2xl font-bold text-amber-700">{totalMaint}</p>
          <p className="text-[10px] text-amber-600 uppercase font-semibold">Maintenance</p>
        </div>
        <div className={cn('rounded-xl border p-4 text-center', occupancyRate > 85 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200')}>
          <p className={cn('text-2xl font-bold', occupancyRate > 85 ? 'text-red-700' : 'text-slate-800')}>{occupancyRate}%</p>
          <p className="text-[10px] text-slate-500 uppercase font-semibold">Occupancy Rate</p>
        </div>
      </div>

      {/* Ward Cards */}
      {loading ? (
        <div className="text-center py-16 text-slate-400">Loading bed status...</div>
      ) : wards.length === 0 ? (
        <div className="text-center py-16 text-slate-400">No wards configured. Set up wards in IPD module.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {wards.map(ward => (
            <div key={ward.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`ward-${ward.id}`}>
              {/* Ward Header */}
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-800">{ward.name}</h3>
                  <p className="text-xs text-slate-500">{ward.ward_type || 'General'} &middot; {ward.total_beds} beds</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{ward.occupied} occupied</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">{ward.vacant} vacant</span>
                </div>
              </div>

              {/* Bed Grid */}
              <div className="p-4">
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {(ward.beds || []).map(bed => {
                    const status = bed.status || 'available';
                    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.available;
                    const Icon = cfg.icon;
                    const isOccupied = status === 'occupied';
                    return (
                      <div key={bed.bed_number}
                        className={cn('rounded-lg border p-2 text-center cursor-pointer transition-all hover:shadow-md group relative', cfg.light)}
                        onClick={() => {
                          // Available / vacant → jump straight to Admissions with this bed pre-selected.
                          // Maintenance → toggle back to available so admins can recover beds quickly.
                          // Occupied → no-op (hover tooltip already shows the patient).
                          if (status === 'available' || status === 'vacant') {
                            const params = new URLSearchParams({
                              ward: ward.id,
                              bed: bed.id,
                              bedNum: bed.bed_number || '',
                              wardName: ward.name || '',
                            });
                            navigate(`/admissions?${params.toString()}`);
                          } else if (status === 'maintenance') {
                            handleStatusChange(ward.id, bed.bed_number, 'available');
                          }
                        }}
                        data-testid={`bed-${ward.id}-${bed.bed_number}`}>
                        <Icon size={16} className="mx-auto mb-1 opacity-70" />
                        <p className="text-xs font-bold">{bed.bed_number}</p>
                        {bed.patient_name && (
                          <p className="text-[9px] mt-0.5 truncate opacity-80">{bed.patient_name}</p>
                        )}
                        {/* Hover tooltip — only for occupied beds */}
                        {isOccupied && (
                          <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1 z-30 hidden group-hover:block whitespace-nowrap bg-slate-900 text-white text-[10px] px-2 py-1.5 rounded-md shadow-lg" data-testid={`bed-tooltip-${ward.id}-${bed.bed_number}`}>
                            <p className="font-semibold">{bed.patient_name || 'Occupied'}</p>
                            {bed.patient_uhid && <p className="opacity-70">UHID: {bed.patient_uhid}</p>}
                            <p className="opacity-70">Bed {bed.bed_number}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 bg-white rounded-xl border border-slate-200 p-3">
        {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'vacant').map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className={cn('h-3 w-3 rounded-full', cfg.color)} />
            <span className="text-xs text-slate-500">{cfg.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BedStatusPage;
